import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { ApiError, api, loginUrl, type Me } from './api';
import { Shell } from './Shell';
import { applyInitialTheme } from './theme';
import './styles.css';

/**
 * The shell resolves the session before rendering anything private. An
 * unauthenticated visitor gets a login prompt rather than an empty dashboard, so
 * a failed session is never mistaken for "there is no content".
 */
function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [state, setState] = useState<'loading' | 'in' | 'out' | 'error'>('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .me()
      .then((u) => {
        setMe(u);
        setState('in');
      })
      .catch((err) => {
        if (err instanceof ApiError && err.isUnauthenticated) {
          setState('out');
          return;
        }
        setError(err instanceof Error ? err.message : String(err));
        setState('error');
      });
  }, []);

  useEffect(() => {
    if (state !== 'in') return;
    let lastCheck = Date.now();
    let pending = false;
    let disposed = false;
    // Reading/scrolling a briefing counts as activity too. There is deliberately
    // no periodic heartbeat: an abandoned tab must still reach its idle limit.
    const onActivity = () => {
      if (document.visibilityState !== 'visible' || pending || Date.now() - lastCheck < 60_000) return;
      pending = true;
      lastCheck = Date.now();
      api.me().then((user) => {
        if (!disposed) setMe(user);
      }).catch((err) => {
        if (!disposed && err instanceof ApiError && err.isUnauthenticated) setState('out');
        // Temporary server/identity-provider failures preserve the session.
      }).finally(() => { pending = false; });
    };
    const events = ['pointerdown', 'keydown', 'scroll', 'focus'] as const;
    for (const event of events) window.addEventListener(event, onActivity, { passive: true, capture: true });
    document.addEventListener('visibilitychange', onActivity);
    return () => {
      disposed = true;
      for (const event of events) window.removeEventListener(event, onActivity, true);
      document.removeEventListener('visibilitychange', onActivity);
    };
  }, [state]);

  if (state === 'loading') {
    return (
      <div className="center-page" role="status">
        <p className="muted">Sitzung wird geprüft…</p>
      </div>
    );
  }

  if (state === 'out') {
    const redirect = window.location.pathname + window.location.search;
    return (
      <div className="center-page">
        <div className="gate">
          <img src="/brand/forest-amber-192.png" alt="" width="96" height="96" />
          <h1>Hugarheim</h1>
          <p className="page-lead">Private Übersicht. Anmeldung erforderlich.</p>
          <p>
            <a className="button primary" href={loginUrl(redirect)}>
              Mit Keycloak anmelden
            </a>
          </p>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="center-page">
        <div className="gate" role="alert">
          <h1>Nicht erreichbar</h1>
          <p className="page-lead">{error}</p>
          <p className="muted small">
            Die Anwendung konnte die Sitzung nicht prüfen. Das ist ein Fehler, nicht ein leerer Tag.
          </p>
        </div>
      </div>
    );
  }

  return (
    <Shell
      me={me}
      onLogout={async () => {
        await api.logout().catch(() => undefined);
        window.location.reload();
      }}
    />
  );
}

applyInitialTheme();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </StrictMode>,
);
